import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';
import { DataService } from '../../../../../../services/dashboard/data.service';
import { AuthService } from '../../../../../../services/auth/auth.service';
import { ToastService } from '../../../../../../services/toast/toast.service';
import { LaunchBannerDismissalService } from '../../../../../../services/dashboard/launch-banner-dismissal.service';
import { NtMedicineLaunchStatusInterface } from '../../../../../../shared/interfaces/nt-medicine-launch-status.interface';

// Compact banner shown to a SUPERUSER on the National Dashboard when the backend says
// the medicine launch can be confirmed (canConfirm). The launch percentage is computed
// by the backend only. "Più tardi" dismisses it per user + company + medicine for the
// current login session (LaunchBannerDismissalService, cleared on login/logout).
@Component({
  selector: 'app-launch-confirmation-banner',
  imports: [CommonModule],
  templateUrl: './launch-confirmation-banner.component.html',
})
export class LaunchConfirmationBannerComponent implements OnChanges, OnDestroy {
  @Input() companyId: number | null = null;
  @Input() medicineId?: number | null;
  @Input() countryId: number | null = null;

  // Emits the medicineId whose launch is confirmed (by this user or, on 409, by another one)
  @Output() launched = new EventEmitter<number>();

  isVisible: boolean = false;
  isConfirming: boolean = false;
  launchPercentage: number = 0;

  // Identifies the medicine/country currently displayed: responses whose key
  // differs belong to an earlier dashboard and are ignored
  private contextKey: string | null = null;
  private statusSub?: Subscription;

  constructor(
    private dataService: DataService,
    private authService: AuthService,
    private toastService: ToastService,
    private dismissalService: LaunchBannerDismissalService,
  ) {}

  ngOnChanges(): void {
    const key = this.buildContextKey();
    // Same identifiers as before: nothing to reload (avoids duplicate requests)
    if (key === this.contextKey) return;

    this.contextKey = key;
    this.isVisible = false;
    this.isConfirming = false;
    this.launchPercentage = 0;
    this.statusSub?.unsubscribe();

    // A medicine dismissed with "Più tardi" in this login session stays hidden:
    // no need to ask the backend for its launch status
    if (key !== null && this.authService.isSuperUser() && !this.isDismissed()) {
      this.loadLaunchStatus();
    }
  }

  ngOnDestroy(): void {
    this.contextKey = null;
    this.statusSub?.unsubscribe();
  }

  confirmLaunch(): void {
    if (this.isConfirming || this.contextKey === null) return;
    this.isConfirming = true;

    const key = this.contextKey;
    const medicineId = this.medicineId as number;

    this.dataService
      .confirmMedicineLaunch(this.companyId as number, medicineId, this.countryId as number)
      .subscribe({
        next: () => {
          if (key !== this.contextKey) return;
          this.isConfirming = false;
          this.isVisible = false;
          this.launched.emit(medicineId);
          this.toastService.success('Il lancio del farmaco è stato confermato con successo.');
        },
        error: (err: HttpErrorResponse) => {
          if (key !== this.contextKey) return;
          this.isConfirming = false;
          this.handleConfirmError(err);
        },
      });
  }

  // Backend percentage capped at 100, at most 2 decimals, no trailing zeros (100.00 -> 100)
  // and Italian decimal comma (99.45 -> 99,45)
  get displayedLaunchPercentage(): string {
    return String(Number(Math.min(this.launchPercentage, 100).toFixed(2))).replace('.', ',');
  }

  dismiss(): void {
    this.isVisible = false;
    if (this.contextKey !== null) {
      this.dismissalService.dismiss(this.companyId as number, this.medicineId as number);
    }
  }

  private isDismissed(): boolean {
    return this.dismissalService.isDismissed(
      this.companyId as number,
      this.medicineId as number,
    );
  }

  private handleConfirmError(err: HttpErrorResponse): void {
    switch (err?.status) {
      case 409:
        this.isVisible = false;
        this.toastService.info('Il lancio del farmaco è già stato confermato da un altro Super User.');
        this.loadLaunchStatus();
        break;
      case 403:
        this.isVisible = false;
        this.toastService.error('Non sei autorizzato a confermare il lancio di questo farmaco.');
        break;
      case 422:
        this.toastService.warning('I requisiti per il lancio del farmaco non sono più soddisfatti.');
        this.loadLaunchStatus();
        break;
      case 404:
        this.isVisible = false;
        this.toastService.error('Farmaco non trovato. Impossibile confermare il lancio.');
        break;
      case 400:
        this.toastService.error('Richiesta non valida. Impossibile confermare il lancio del farmaco.');
        break;
      default:
        // Network/server errors: the banner stays visible so the user can retry
        this.toastService.error('Impossibile confermare il lancio del farmaco. Riprova più tardi.');
        break;
    }
  }

  private loadLaunchStatus(): void {
    const key = this.contextKey;
    this.statusSub?.unsubscribe();
    this.statusSub = this.dataService
      .getMedicineLaunchStatus(
        this.companyId as number,
        this.medicineId as number,
        this.countryId as number,
      )
      .subscribe({
        next: (status: NtMedicineLaunchStatusInterface) => {
          if (key !== this.contextKey) return;
          this.launchPercentage = status?.launchPercentage ?? 0;
          if (status?.isLaunched) {
            this.launched.emit(this.medicineId as number);
          }
          this.isVisible =
            status?.canConfirm === true && !status?.isLaunched && !this.isDismissed();
        },
        error: (err) => {
          if (key !== this.contextKey) return;
          this.isVisible = false;
          console.error('Error loading medicine launch status', err);
        },
      });
  }

  private buildContextKey(): string | null {
    if (!this.companyId || !this.medicineId || !this.countryId) return null;
    return `${this.companyId}-${this.medicineId}-${this.countryId}`;
  }
}
